import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Ingredient } from '../types/Types';
import axios from 'axios';

const DEFAULT_SEARCH_LIMIT = 5;

export interface IngredientByItemResult {
  ingredient: Ingredient;
  materialIngredients: any[];
}

// Optional AND filters for narrowing an ingredient search — all empty/undefined values are ignored.
export interface IngredientSearchFilters {
  accountingType?: string;
  primaryClass?: string;
  secondaryClass?: string;
  subprocess?: string;
  tab?: 'active' | 'archive';
}

interface IngredientCacheContextType {
  // Cache management
  getIngredient: (id: string) => Ingredient | null;
  addIngredientsToCache: (ingredients: Ingredient[]) => void;
  clearSearchCache: () => void;
  isLoading: boolean;

  // Search functionality with infinite scroll
  searchIngredients: (
    query: string,
    page?: number,
    filters?: IngredientSearchFilters,
    limit?: number
  ) => Promise<{ ingredients: Ingredient[]; hasMore: boolean }>;

  // Fetch single ingredient by ID (checks cache first)
  fetchIngredientById: (id: string) => Promise<Ingredient | null>;

  // Fetch ingredient by item ID with materials (checks cache first)
  fetchIngredientByItemId: (itemId: string) => Promise<IngredientByItemResult | null>;

  // Fetch ingredient by ingredient ID with materials (checks by-item cache first)
  fetchIngredientByIdWithMaterials: (id: string) => Promise<IngredientByItemResult | null>;

  // Initialize
  initialize: (getIngredientByIdFn: (id: string) => Promise<any>) => void;
}

const IngredientCacheContext = createContext<IngredientCacheContextType | undefined>(undefined);

export const IngredientCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ingredientCache, setIngredientCache] = useState<Map<string, Ingredient>>(new Map());
  const [searchResults, setSearchResults] = useState<Map<string, Ingredient[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [getIngredientByIdFn, setGetIngredientByIdFn] = useState<((id: string) => Promise<any>) | null>(null);

  // Use refs to access caches without causing re-renders in dependent useEffects
  const ingredientCacheRef = useRef<Map<string, Ingredient>>(ingredientCache);
  const searchResultsRef = useRef<Map<string, Ingredient[]>>(searchResults);

  // New caches for by-item and by-id-with-materials lookups
  const byItemCacheRef = useRef<Map<string, IngredientByItemResult>>(new Map());
  const byIdWithMaterialsCacheRef = useRef<Map<string, IngredientByItemResult>>(new Map());

  useEffect(() => {
    ingredientCacheRef.current = ingredientCache;
  }, [ingredientCache]);

  useEffect(() => {
    searchResultsRef.current = searchResults;
  }, [searchResults]);

  const initialize = useCallback((getIngredientByIdFn: (id: string) => Promise<any>) => {
    setGetIngredientByIdFn(() => getIngredientByIdFn);
  }, []);

  // STABLE function reference - uses ref to access cache
  const getIngredient = useCallback((id: string): Ingredient | null => {
    return ingredientCacheRef.current.get(id) || null;
  }, []); // No dependencies - stable reference

  const addIngredientsToCache = useCallback((ingredients: Ingredient[]) => {
    setIngredientCache(prev => {
      const newCache = new Map(prev);
      ingredients.forEach(ingredient => {
        if (ingredient && ingredient._id) {
          newCache.set(ingredient._id, ingredient);
        }
      });
      return newCache;
    });
  }, []);

  const clearSearchCache = useCallback(() => {
    setSearchResults(new Map());
  }, []);

  // STABLE function reference - Fetch single ingredient by ID - checks cache first, then fetches from API
  const fetchIngredientById = useCallback(async (id: string): Promise<Ingredient | null> => {
    // Check cache first (use ref for stable check)
    const cached = ingredientCacheRef.current.get(id);
    if (cached &&
        cached.items && typeof cached.items === 'object' && (cached.items as any).ident) {
      return cached;
    }

    // Fetch from API
    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await axios.get(`${apiUrl}/api/ingredients/${id}`, {
        headers: { 'x-auth-token': token }
      });

      const ingredient = response.data;
      if (ingredient && ingredient.items && typeof ingredient.items === 'object') {
        // Add to cache
        setIngredientCache(prev => {
          const newCache = new Map(prev);
          newCache.set(ingredient._id, ingredient);
          return newCache;
        });
        return ingredient;
      }

      return null;
    } catch (error) {
      return null;
    }
  }, []); // No dependencies - uses ref for stable reference

  // STABLE function reference - uses ref to access search cache
  const searchIngredients = useCallback(async (
    query: string,
    page: number = 1,
    filters?: IngredientSearchFilters,
    limit: number = DEFAULT_SEARCH_LIMIT
  ): Promise<{ ingredients: Ingredient[]; hasMore: boolean }> => {
    // Serialize non-empty filters into the cache key so different filter
    // combinations never collide in the search cache.
    const filterKey = filters
      ? Object.entries(filters)
          .filter(([, v]) => !!v)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, v]) => `${k}=${v}`)
          .join('&')
      : '';
    const cacheKey = `${query.toLowerCase().trim()}_page_${page}_filters_${filterKey}_lim_${limit}`;
    const hasMoreKey = `${cacheKey}_hasMore`;

    // Return cached result if available (use ref for stable check)
    if (searchResultsRef.current.has(cacheKey)) {
      const cachedIngredients = searchResultsRef.current.get(cacheKey)!;
      const cachedHasMore = searchResultsRef.current.get(hasMoreKey);
      return {
        ingredients: cachedIngredients,
        hasMore: cachedHasMore ? true : cachedIngredients.length === limit
      };
    }

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const params: Record<string, string | number> = {
        search: query,
        sort: 'createdAt',
        order: 'desc',
        page,
        limit,
      };
      if (filters?.accountingType) params.accountingType = filters.accountingType;
      if (filters?.primaryClass) params.primaryClass = filters.primaryClass;
      if (filters?.secondaryClass) params.secondaryClass = filters.secondaryClass;
      if (filters?.subprocess) params.subprocess = filters.subprocess;
      if (filters?.tab) params.tab = filters.tab;

      const response = await axios.get(`${apiUrl}/api/ingredients`, {
        headers: { 'x-auth-token': token },
        params,
      });

      const newIngredients: Ingredient[] = response.data.ingredients || [];
      const totalPages = response.data.totalPages || 1;
      const hasMore = page < totalPages;

      // Cache the result and hasMore flag
      setSearchResults(prev => {
        const newCache = new Map(prev);
        newCache.set(cacheKey, newIngredients);
        if (hasMore) {
          newCache.set(hasMoreKey, [{ hasMore: true }] as any); // Store hasMore flag
        }
        return newCache;
      });

      // Cache individual ingredients
      setIngredientCache(prev => {
        const newCache = new Map(prev);
        newIngredients.forEach(ingredient => {
          if (ingredient && ingredient._id) {
            newCache.set(ingredient._id, ingredient);
          }
        });
        return newCache;
      });

      return { ingredients: newIngredients, hasMore };

    } catch (error) {
      return { ingredients: [], hasMore: false };
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies - uses ref for stable reference (filters passed in per-call)

  // Fetch ingredient by item ID with materialIngredients — caches under itemId
  const fetchIngredientByItemId = useCallback(async (itemId: string): Promise<IngredientByItemResult | null> => {
    // Check by-item cache first
    const cached = byItemCacheRef.current.get(itemId);
    if (cached) return cached;

    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await axios.get(`${apiUrl}/api/ingredients/by-item/${itemId}`, {
        headers: { 'x-auth-token': token },
        params: { includeMaterials: 'true' },
      });

      if (!response.data || !response.data.ingredient) return null;

      const result: IngredientByItemResult = {
        ingredient: response.data.ingredient,
        materialIngredients: response.data.materialIngredients || [],
      };

      // Store in by-item cache (keyed by item id)
      byItemCacheRef.current.set(itemId, result);

      // Also populate by-id-with-materials cache (keyed by ingredient _id)
      byIdWithMaterialsCacheRef.current.set(result.ingredient._id, result);

      // Populate the base ingredient cache too
      setIngredientCache(prev => {
        const newCache = new Map(prev);
        newCache.set(result.ingredient._id, result.ingredient);
        return newCache;
      });

      return result;
    } catch {
      return null;
    }
  }, []); // stable — uses refs

  // Fetch ingredient by ingredient ID with materialIngredients — checks by-item cache first
  const fetchIngredientByIdWithMaterials = useCallback(async (id: string): Promise<IngredientByItemResult | null> => {
    // Check by-id-with-materials cache first
    const cachedById = byIdWithMaterialsCacheRef.current.get(id);
    if (cachedById) return cachedById;

    // Scan by-item cache for a matching ingredient _id
    const byItemEntries = Array.from(byItemCacheRef.current.values());
    for (const entry of byItemEntries) {
      if (entry.ingredient._id === id) {
        // Also store under ingredient _id for next time
        byIdWithMaterialsCacheRef.current.set(id, entry);
        return entry;
      }
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await axios.get(`${apiUrl}/api/ingredients/${id}`, {
        headers: { 'x-auth-token': token },
        params: { includeMaterials: 'true' },
      });

      if (!response.data || !response.data.ingredient) return null;

      const result: IngredientByItemResult = {
        ingredient: response.data.ingredient,
        materialIngredients: response.data.materialIngredients || [],
      };

      // Store in by-id-with-materials cache
      byIdWithMaterialsCacheRef.current.set(id, result);

      // Also store in by-item cache if we know the item id
      const itemId = typeof result.ingredient.items === 'object'
        ? (result.ingredient.items as any)?._id
        : result.ingredient.items;
      if (itemId) {
        byItemCacheRef.current.set(itemId as string, result);
      }

      // Populate base ingredient cache
      setIngredientCache(prev => {
        const newCache = new Map(prev);
        newCache.set(result.ingredient._id, result.ingredient);
        return newCache;
      });

      return result;
    } catch {
      return null;
    }
  }, []); // stable — uses refs

  const value: IngredientCacheContextType = {
    getIngredient,
    addIngredientsToCache,
    clearSearchCache,
    isLoading,
    searchIngredients,
    fetchIngredientById,
    fetchIngredientByItemId,
    fetchIngredientByIdWithMaterials,
    initialize,
  };

  return <IngredientCacheContext.Provider value={value}>{children}</IngredientCacheContext.Provider>;
};

export const useIngredientCache = (): IngredientCacheContextType => {
  const context = useContext(IngredientCacheContext);
  if (!context) {
    throw new Error('useIngredientCache must be used within IngredientCacheProvider');
  }
  return context;
};
